import { array, func } from 'prop-types';
import { useImmer } from 'use-immer';
import React, { useCallback, useState } from 'react';
import Button from 'react-bootstrap/Button';
import FuzzySearch from 'fuzzy-search';
import Container from 'react-bootstrap/Container';
import FormControl from 'react-bootstrap/FormControl';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { MdEdit, MdPermContactCalendar, MdClear } from 'react-icons/md';
import ReactSlidingPane from '../../components/ReactSlidingPane';
import ExternalContactForm from './ExternalContactForm';
import { ExternalContactLabel } from './SecureShareLeftPanel';
import { useExternalContact } from '../../hooks/useExternalContacts';
import DataTable from 'react-data-table-component';
import { phypalDataTableCustomStyles } from '../Utils/datatable';
import { getKeyName } from '../helpers/getKeyName';

export const FilterComponent = ({ filterText = '', onFilter, onClear, placeholder = '' }) => (
  <div className={'d-flex align-items-center w-100'}>
    <FormControl fullWidth id="search" type="text" placeholder={placeholder || 'Filter By Name, Email & Type'} aria-label="Search Input" value={filterText} onChange={onFilter} />
    {filterText.length > 0 && (
      <span
        style={{
          position: 'relative',
          left: '-40px',
          zIndex: 100,
          width: 0,
          cursor: 'pointer',
        }}
      >
        <MdClear fontSize={24} onClick={onClear} />
      </span>
    )}
  </div>
);

export default function ExternalContactTable({ data = [], fetchDocs }) {
  const [filterText, setFilterText] = React.useState('');
  const [resetPaginationToggle, setResetPaginationToggle] = React.useState(false);
  const { isExternalContact } = useExternalContact();
  const [state, setState] = useImmer({
    contactPane: false,
    edit: false,
    activeContact: null,
  });

  const searcher = new FuzzySearch(data, ['firstName', 'lastName', 'email', 'type']);

  const filteredExternalContact = searcher.search(filterText);

  const handleContactPaneClose = useCallback(
    () =>
      setState((draft) => {
        draft.contactPane = false;
        draft.edit = false;
        draft.activeContact = null;
      }),
    [setState],
  );

  const columns = [
    { name: 'Contact Name', selector: (row) => getKeyName(row, ['firstName', 'lastName']), sortable: true },
    { name: 'Email', selector: (row) => row?.email, sortable: true },
    { name: 'Type', cell: (row) => row?.type, sortable: true },
    {
      name: 'Edit',
      center: true,
      cell: (row) => {
        return (
          !isExternalContact && (
            <MdEdit
              size="1.3em"
              onClick={() => {
                setState((draft) => {
                  draft.edit = true;
                  draft.activeContact = row;
                  draft.contactPane = true;
                });
              }}
              className="csr-pointer"
            />
          )
        );
      },
    },
  ];

  const subHeaderComponentMemo = React.useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText('');
      }
    };

    return <FilterComponent onFilter={(e) => setFilterText(e.target.value)} onClear={handleClear} filterText={filterText} />;
  }, [filterText, resetPaginationToggle]);

  // console.log(new Set(data.map((row) => row.type)));

  return (
    <Container fluid>
      <div className={'mb-3 d-flex flex-row justify-content-between align-items-center'}>
        {ExternalContactLabel}
        {!isExternalContact && (
          <div className={'d-flex'}>
            {subHeaderComponentMemo}
            <Button
              size={'sm'}
              variant={'fff'}
              className={'d-flex align-items-center ml-2'}
              style={{ minWidth: 160 }}
              onClick={() => {
                setState((draft) => {
                  draft.contactPane = true;
                  draft.edit = false;
                  draft.activeContact = null;
                });
              }}
            >
              <MdPermContactCalendar size={'1.3em'} className={'mr-2'} />
              Add New Contact
            </Button>
          </div>
        )}
      </div>
      <DataTable
        paginationResetDefaultPage={resetPaginationToggle}
        // subHeader
        // subHeaderComponent={subHeaderComponentMemo}
        data={filteredExternalContact}
        columns={columns}
        fixedHeader
        fixedHeaderScrollHeight={'65vh'}
        persistTableHead
        pagination={true}
        customStyles={phypalDataTableCustomStyles}
      />
      <ReactSlidingPane title={'Contact Details'} width={'40%'} state={state.contactPane} onClose={handleContactPaneClose}>
        <ExternalContactForm
          onCancelCb={handleContactPaneClose}
          onSaveCb={() => {
            handleContactPaneClose();
            fetchDocs();
          }}
          edit={state.edit}
          contact={state.activeContact}
        />
      </ReactSlidingPane>
    </Container>
  );
}

ExternalContactTable.propTypes = {
  data: array,
  fetchDocs: func,
};
